import React from 'react';
import { Text, Tooltip } from '@mantine/core';
import {
    IconAlertTriangle,
    IconCircleCheck,
    IconInfoCircle,
    IconSquareRoundedX,
} from '@tabler/icons-react';

import { Request } from '../../codegen/graphql';
import formatCurrency from '../../utility/formatCurrency';
import { getCurrencyFromRequest } from '../../utility/getProductCurrency';
import { getProposedBelowCostMessage } from '../../utility/surveyUtils';
import { useAppConfig } from '../../hooks/useAppConfig';
import { formatPercent } from './requestsTableRenderers';
import {
    getInterimPriceForRequest,
    getProposedMarginIndicatorModel,
    getRequestHighestCostPrice,
} from '../../helpers/pricingBatchIndicatorHelpers';
import {
    getRequestStoreCostPrice,
    getRequestZoneHighestCostPrice,
    isSingleSuggestedStoreRequest,
} from '../../helpers/requestZonePricing';

export const PRICING_BATCH_TABLE_VALUE_CLASS =
    'text-left font-normal tabular-nums text-[var(--mantine-color-dark-7)]';

const CELL_DIM_CLASS = 'text-left font-normal tabular-nums text-[#7D8FB3]';

const VALUE_ROW_CLASS = 'flex h-6 min-w-0 items-center gap-1.5';

const ICON_SEMANTIC =
    'inline-flex h-4 w-4 shrink-0 items-center justify-center';

type IndicatorBaseProps = {
    request: Request;
    hideCostAndMargins: boolean;
};

type ProposedPriceCellProps = IndicatorBaseProps & {
    vatPercentage: number;
    numericAlignment?: 'end' | 'start';
};

export function PricingBatchInterimPriceCell({
    request,
}: {
    request: Request;
}) {
    const currency = getCurrencyFromRequest(request);
    const amount = getInterimPriceForRequest(request);
    const valueClass =
        amount != null
            ? PRICING_BATCH_TABLE_VALUE_CLASS
            : 'text-left font-normal tabular-nums text-[var(--mantine-color-dimmed)]';
    return (
        <div className={VALUE_ROW_CLASS}>
            <Text size="sm" className={valueClass}>
                {amount != null ? formatCurrency(amount, currency) : '—'}
            </Text>
        </div>
    );
}

export function PricingBatchProposedPriceCell({
    request,
    vatPercentage,
    hideCostAndMargins,
    numericAlignment = 'start',
}: ProposedPriceCellProps) {
    const currency = getCurrencyFromRequest(request);
    const amount = request.proposedPrice ?? 0;
    const formatted = formatCurrency(amount, currency);

    const highestCost = getRequestHighestCostPrice(request);
    const belowCost =
        !hideCostAndMargins &&
        getProposedBelowCostMessage(
            highestCost,
            request.proposedPrice,
            vatPercentage,
        ) !== null;

    const valueClass =
        numericAlignment === 'end'
            ? PRICING_BATCH_TABLE_VALUE_CLASS
            : 'text-left font-normal tabular-nums text-[var(--mantine-color-dark-7)]';

    if (!belowCost) {
        return (
            <div className={VALUE_ROW_CLASS}>
                <Text size="sm" className={valueClass}>
                    {formatted}
                </Text>
            </div>
        );
    }

    const belowCostValueClass =
        numericAlignment === 'end'
            ? 'text-right font-normal tabular-nums text-red-600'
            : 'text-left font-normal tabular-nums text-red-600';

    return (
        <div className={VALUE_ROW_CLASS}>
            <Text size="sm" className={belowCostValueClass}>
                {formatted}
            </Text>
            <IconSquareRoundedX
                className={`${ICON_SEMANTIC} text-red-600`}
                size={14}
            />
            <Tooltip
                label="The proposed price is below cost (ex VAT)"
                withArrow
                position="top"
                zIndex={2000}
            >
                <span className={`${ICON_SEMANTIC} cursor-help text-[#7D8FB3]`}>
                    <IconInfoCircle size={14} aria-hidden />
                </span>
            </Tooltip>
        </div>
    );
}

export function PricingBatchCostPriceCell({
    request,
    hideCostAndMargins,
}: IndicatorBaseProps) {
    const currency = getCurrencyFromRequest(request);
    const zoneCost = getRequestHighestCostPrice(request) ?? 0;
    const formatted = formatCurrency(zoneCost, currency);
    const storeCost = getRequestStoreCostPrice(request);
    const zoneHighest = getRequestZoneHighestCostPrice(request);
    const showingZoneHighest =
        zoneHighest != null && Math.abs(zoneCost - zoneHighest) <= 0.005;

    const showStoreCostHint =
        !hideCostAndMargins &&
        !isSingleSuggestedStoreRequest(request) &&
        showingZoneHighest &&
        storeCost != null &&
        Number.isFinite(storeCost) &&
        Number.isFinite(zoneCost) &&
        Math.abs(storeCost - zoneCost) > 0.005;

    if (!showStoreCostHint) {
        return (
            <div className={VALUE_ROW_CLASS}>
                <Text size="sm" className={PRICING_BATCH_TABLE_VALUE_CLASS}>
                    {formatted}
                </Text>
            </div>
        );
    }

    const storeFormatted = formatCurrency(storeCost, currency);

    return (
        <div className={VALUE_ROW_CLASS}>
            <Text size="sm" className={PRICING_BATCH_TABLE_VALUE_CLASS}>
                {formatted}
            </Text>
            <Tooltip
                label={
                    <>
                        <strong>Store cost price:</strong> {storeFormatted}
                        <br />
                        <strong>Highest in AZ:</strong> {formatted}
                    </>
                }
                multiline
                w={260}
                withArrow
                position="top"
                zIndex={2000}
            >
                <span className={`${ICON_SEMANTIC} cursor-help text-[#7D8FB3]`}>
                    <IconInfoCircle size={14} aria-hidden />
                </span>
            </Tooltip>
        </div>
    );
}

export function PricingBatchCurrentMarginCell({
    request,
}: {
    request: Request;
}) {
    return (
        <div className={VALUE_ROW_CLASS}>
            <Text size="sm" className={PRICING_BATCH_TABLE_VALUE_CLASS}>
                {formatPercent(request.currentMargin)}
            </Text>
        </div>
    );
}

export function PricingBatchProposedMarginCell({
    request,
    hideCostAndMargins,
}: IndicatorBaseProps) {
    const { vatPercentage: fallbackVatPercentage } = useAppConfig();

    if (hideCostAndMargins) {
        return (
            <div className={VALUE_ROW_CLASS}>
                <Text size="sm" className={PRICING_BATCH_TABLE_VALUE_CLASS}>
                    {formatPercent(request.proposedMargin)}
                </Text>
            </div>
        );
    }

    const { marginValue, isMarginPositive } = getProposedMarginIndicatorModel(
        request,
        fallbackVatPercentage,
    );

    if (!Number.isFinite(marginValue)) {
        return (
            <div className={VALUE_ROW_CLASS}>
                <Text size="sm" className={CELL_DIM_CLASS}>
                    N/A
                </Text>
            </div>
        );
    }

    const valueClass = isMarginPositive
        ? 'text-left font-normal tabular-nums text-green-600'
        : 'text-left font-normal tabular-nums text-amber-600';
    const iconClass = isMarginPositive ? 'text-green-500' : 'text-amber-500';
    const IndicatorIcon = isMarginPositive
        ? IconCircleCheck
        : IconAlertTriangle;

    return (
        <div className={VALUE_ROW_CLASS}>
            <Text size="sm" className={valueClass}>
                {formatPercent(marginValue)}
            </Text>
            <IndicatorIcon
                className={`${ICON_SEMANTIC} ${iconClass}`}
                size={14}
            />
        </div>
    );
}

export function PricingBatchCurrencyAmountCell({
    request,
    amount,
}: {
    request: Request;
    amount: number;
}) {
    const currency = getCurrencyFromRequest(request);
    return (
        <div className={VALUE_ROW_CLASS}>
            <Text size="sm" className={PRICING_BATCH_TABLE_VALUE_CLASS}>
                {formatCurrency(amount, currency)}
            </Text>
        </div>
    );
}
